import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { X, ExternalLink, Megaphone, AlertTriangle, Gift, Bell } from 'lucide-react';
import axios from 'axios';
import { cn } from '../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const NewsBanner = () => {
  const { user, token } = useAuth();
  const [news, setNews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Show for all authenticated users
    if (user && token) {
      fetchNews();
    } else {
      setLoading(false);
    }
  }, [user, token]);

  useEffect(() => {
    // Auto-rotate news every 5 seconds if multiple
    if (news.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % news.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [news.length]);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/client-news/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out dismissed news
      const dismissedIds = JSON.parse(localStorage.getItem('dismissedNews') || '[]');
      const activeNews = (response.data || []).filter(n => !dismissedIds.includes(n.id));
      setNews(activeNews);
      setDismissed(dismissedIds);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = (newsId) => {
    const newDismissed = [...dismissed, newsId];
    setDismissed(newDismissed);
    localStorage.setItem('dismissedNews', JSON.stringify(newDismissed));
    setNews(news.filter(n => n.id !== newsId));
    if (currentIndex >= news.length - 1) {
      setCurrentIndex(0);
    }
  };

  const getTypeConfig = (type, customBgColor) => {
    // If custom color is provided, use it
    if (customBgColor) {
      return {
        bg: '',
        bgStyle: { backgroundColor: customBgColor },
        icon: type === 'warning' ? AlertTriangle : type === 'promo' ? Gift : type === 'update' ? Bell : Megaphone,
        textColor: 'text-white'
      };
    }
    
    switch (type) {
      case 'warning':
        return {
          bg: 'bg-amber-500',
          bgStyle: {},
          icon: AlertTriangle,
          textColor: 'text-white'
        };
      case 'promo':
        return {
          bg: 'bg-gradient-to-r from-purple-500 to-pink-500',
          bgStyle: {},
          icon: Gift,
          textColor: 'text-white'
        };
      case 'update':
        return {
          bg: 'bg-blue-500',
          bgStyle: {},
          icon: Bell,
          textColor: 'text-white'
        };
      default: // info
        return {
          bg: 'bg-gradient-to-r from-emerald-500 to-teal-500',
          bgStyle: {},
          icon: Megaphone,
          textColor: 'text-white'
        };
    }
  };

  const getAudienceLabel = (audience) => {
    switch (audience) {
      case 'members': return 'Équipe';
      case 'subcontractors': return 'Sous-traitants';
      case 'clients': return 'Clients';
      case 'all': return 'Tous';
      default: return '';
    }
  };

  if (loading || news.length === 0 || !user) {
    return null;
  }

  const currentNews = news[currentIndex];
  const config = getTypeConfig(currentNews?.type, currentNews?.bg_color);
  const IconComponent = config.icon;

  // Build style object for background
  const bannerStyle = {
    ...config.bgStyle
  };
  
  // If there's a background image, add it
  if (currentNews?.bg_image_url) {
    bannerStyle.backgroundImage = `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${currentNews.bg_image_url})`;
    bannerStyle.backgroundSize = 'cover';
    bannerStyle.backgroundPosition = 'center';
  }

  return (
    <div 
      className={cn(
        "w-full py-2 px-4",
        config.bg,
        config.textColor
      )}
      style={bannerStyle}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <IconComponent className="w-5 h-5 shrink-0" />
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <span className="font-semibold shrink-0">{currentNews.title}</span>
            <span className="text-sm opacity-90 truncate hidden sm:block">{currentNews.content}</span>
            {currentNews.link_url && (
              <a 
                href={currentNews.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 flex items-center gap-1 text-sm font-medium underline underline-offset-2 hover:opacity-80"
              >
                {currentNews.link_text || 'En savoir plus'}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2 shrink-0">
          {/* Pagination dots if multiple news */}
          {news.length > 1 && (
            <div className="flex gap-1">
              {news.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all",
                    idx === currentIndex ? "bg-white" : "bg-white/40"
                  )}
                />
              ))}
            </div>
          )}
          
          <button
            onClick={() => handleDismiss(currentNews.id)}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
            title="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsBanner;
